import { Box, Typography, Avatar } from '@mui/material';
import { useState } from 'react';
import type { appDeltaItem } from '../../types/pointhome.types';

type Props = { topUps: appDeltaItem[]; topDowns: appDeltaItem[] };

const krw = (n: number) =>
  new Intl.NumberFormat('ko-KR', { style: 'currency', currency: 'KRW', maximumFractionDigits: 0 }).format(n);

function Pill({ item, dir }: { item: appDeltaItem; dir: 'up' | 'down' }) {
  const [imageError, setImageError] = useState(false);
  const pct = item.prev > 0 ? Math.round((item.diff / item.prev) * 100) : null;
  const color = dir === 'up' ? '#D32F2F' : '#1976D2';
  const arrow = dir === 'up' ? '▲' : '▼';

  const appImagePath = `/img/adpopcorn/app/${item.appName}.png`;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: { xs: 1, sm: 1.25 },
        p: { xs: 1, sm: 1.25 },
        borderRadius: 9999,
        border: '1px solid #E0E3E7',
        bgcolor: '#fff',
        minWidth: 0,
        overflow: 'hidden',
      }}
    >
      <Avatar
        src={!imageError ? appImagePath : undefined}
        onError={() => setImageError(true)}
        sx={{
          width: { xs: 28, sm: 36, md: 40 },
          height: { xs: 28, sm: 36, md: 40 },
          bgcolor: '#EEF2F6',
          color: '#455A64',
          fontWeight: 700,
          fontSize: { xs: 12, sm: 14 },
          flexShrink: 0,
        }}
      >
        {imageError ? (item.appName?.[0] ?? '?') : null}
      </Avatar>

      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography variant="subtitle2" noWrap title={item.appName}>
          {item.appName}
        </Typography>
        <Typography variant="subtitle2" fontWeight={700}>
          {krw(Math.abs(item.curr))}
        </Typography>
      </Box>

      {pct !== null && (
        <Box
          sx={{
            display: { xs: 'none', sm: 'flex' },
            alignItems: 'center',
            gap: 0.5,
            color,
            fontWeight: 700,
            whiteSpace: 'nowrap',
            flexShrink: 0,
          }}
        >
          <span>{arrow}</span>
          <span>{Math.abs(pct)}%</span>
        </Box>
      )}
    </Box>
  );
}

const GRID5 = {
  display: 'grid',
  gridTemplateColumns: 'repeat(5, minmax(0, 1fr))',
  gap: { xs: 1, sm: 1.5 },
} as const;

function Section({
  title,
  items,
  dir,
  emptyText,
}: {
  title: string;
  items: appDeltaItem[];
  dir: 'up' | 'down';
  emptyText: string;
}) {
  return (
    <Box sx={{ mt: 2, overflowX: 'hidden' }}>
      <Typography variant="subtitle1" sx={{ mb: 1 }}>
        {title}
      </Typography>
      <Box sx={GRID5}>
        {items.length === 0 ? (
          <Box
            sx={{
              gridColumn: '1 / -1',
              p: 1.25,
              borderRadius: 1,
              border: '1px dashed #E0E3E7',
              bgcolor: '#FAFAFA',
            }}
            aria-live="polite"
          >
            <Typography variant="body2" color="text.secondary">
              {emptyText}
            </Typography>
          </Box>
        ) : (
          <>
            {items.slice(0, 5).map((it) => (
              <Pill key={`${dir}-${it.appName}`} item={it} dir={dir} />
            ))}
            {Array.from({ length: Math.max(0, 5 - items.length) }).map((_, i) => (
              <Box key={`${dir}-empty-${i}`} />
            ))}
          </>
        )}
      </Box>
    </Box>
  );
}

export default function PointhomeAppDeltaTop5({ topUps, topDowns }: Props) {
  return (
    <Box sx={{ mt: 2, overflowX: 'hidden' }}>
      <Section title="매출 상승 TOP 5" items={topUps} dir="up" emptyText="상승한 앱이 없습니다." />
      <Section title="매출 하락 TOP 5" items={topDowns} dir="down" emptyText="하락한 앱이 없습니다." />
    </Box>
  );
}
